/**
 * Side-by-side review view. Replaces the live canvas while a proposal is
 * being reviewed, showing the current draft on the left and the proposed
 * snapshot on the right with rows aligned by node id.
 *
 * The user stages per-node accept/reject decisions locally via the controls
 * on each change row and then commits the whole set in a single apply. The
 * server composes the final snapshot from the current draft plus the chosen
 * subset of changes (see `composeAcceptedSnapshot`).
 */

import { useCallback, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  X,
  Plus,
  Minus,
  PencilSimple,
  CaretDown,
  Check,
} from '@phosphor-icons/react';
import { NodeIconPill, Text, Flex, Spinner, PillToggle, useToast, type PillToggleOption } from '@chatsdet/ui';
import { useQueryClient } from '@tanstack/react-query';
import {
  useProposal,
  useApplyProposal,
  useRejectProposal,
} from './useWorkflowProposals';
import { NodeConfigView } from './NodeConfigView';
import { ReviewScorecard } from '../components/ReviewScorecard';
import {
  diffSnapshots,
  fromApiSnapshot,
  fromClientState,
  type SnapshotDiff,
} from './diffSnapshot';
import {
  alignRows,
  type AlignedRow,
  type BranchLabel,
  type NodeWithBranch,
  type RowState,
} from './alignRows';
import { getNodeDisplayTitle } from '@chatsdet/workflow-diff';
import {
  useReviewDecisions,
  type Decision,
  type ReviewDecisionsApi,
} from './useReviewDecisions';
import {
  ReviewActionBar,
  ReviewExpandCollapseControl,
  ReviewHeader,
} from './ProposalReviewChrome';
import type { ApiError } from '../../../../lib/api-error';
import type { WorkflowEdge, WorkflowNode } from '../../../../types/workflow';
import type { AttachedEffect } from '../hooks/useUndoHistory';

interface ProposalReviewViewProps {
  workflowId: string;
  proposalId: string;
  currentNodes: WorkflowNode[];
  currentEdges: WorkflowEdge[];
  onExit: () => void;
  /**
   * Push a snapshot onto the canvas undo stack. The proposal review calls
   * this just before applying so Cmd+Z restores the pre-accept state AND
   * flips the proposal back to pending via the attached effect.
   */
  captureUndoSnapshot?: (
    description: string,
    options?: { attachedEffects?: AttachedEffect[] },
  ) => void;
}

export function ProposalReviewView({
  workflowId,
  proposalId,
  currentNodes,
  currentEdges,
  onExit,
  captureUndoSnapshot,
}: ProposalReviewViewProps) {
  const queryClient = useQueryClient();
  const { data: proposal, isLoading } = useProposal(workflowId, proposalId);
  const { toast } = useToast();
  const applyMutation = useApplyProposal(workflowId);
  const rejectMutation = useRejectProposal(workflowId);
  // Staleness can be discovered three ways: server-authored status on load,
  // the PROPOSAL_STALE error during apply, or a 409. Track the sticky bit
  // locally but initialize it from proposal.status so a proposal that was
  // already marked stale shows the banner immediately.
  const [applyStale, setApplyStale] = useState(false);
  const stale = applyStale || proposal?.status === 'stale';
  const setStale = setApplyStale;

  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const toggleExpanded = useCallback((key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);
  const expandAll = useCallback(
    (keys: string[]) => setExpanded(new Set(keys)),
    [],
  );
  const collapseAll = useCallback(() => setExpanded(new Set()), []);

  const { rows, diff } = useMemo(() => {
    if (!proposal) {
      return {
        rows: [] as AlignedRow[],
        diff: null as SnapshotDiff | null,
      };
    }
    const current = fromClientState(currentNodes, currentEdges);
    const proposed = fromApiSnapshot(proposal.snapshotJson ?? {});
    const d = diffSnapshots(current, proposed);
    const rs = alignRows(
      current.nodes,
      proposed.nodes,
      d,
      current.edges,
      proposed.edges,
    );
    return { rows: rs, diff: d };
  }, [proposal, currentNodes, currentEdges]);

  const decisions = useReviewDecisions(diff, proposalId);

  const apply = useCallback(() => {
    if (!diff) return;
    // If the user accepted *every* change we send the atomic path (no body),
    // which lets the server use the simple, battle-tested accept flow.
    const allIds = new Set<string>([
      ...diff.addedNodes.map((n) => n.id),
      ...diff.removedNodes.map((n) => n.id),
      ...diff.changedNodes.map((n) => n.id),
    ]);
    const sameAsAll =
      decisions.acceptedNodeIds.length === allIds.size &&
      decisions.acceptedNodeIds.every((id) => allIds.has(id));

    // Snapshot the pre-accept canvas onto the undo stack. On Cmd+Z the
    // canvas rolls back AND the proposal flips from accepted → pending via
    // the attached effect.
    if (captureUndoSnapshot && proposal) {
      captureUndoSnapshot(`Accept proposal: ${proposal.title}`, {
        attachedEffects: [
          {
            kind: 'revert-proposal',
            proposalId,
            proposalTitle: proposal.title,
          },
        ],
      });
    }

    applyMutation.mutate(
      {
        proposalId,
        acceptedNodeIds: sameAsAll ? null : decisions.acceptedNodeIds,
      },
      {
        onSuccess: () => {
          toast.success(
            decisions.counts.accepted === decisions.counts.total
              ? 'Proposal applied to the draft.'
              : `Applied ${decisions.counts.accepted} of ${decisions.counts.total} changes.`,
          );
          onExit();
        },
        onError: (err) => {
          const e = err as ApiError;
          switch (e.code) {
            case 'PROPOSAL_STALE':
              setStale(true);
              return;
            case 'PROPOSAL_ALREADY_DECIDED':
              // Another tab/user got here first; close out and let the list
              // refresh — the proposal is no longer reviewable.
              toast.default('This proposal was just decided by someone else.');
              onExit();
              return;
            case 'PROPOSAL_SELECTION_INVALID':
              toast.error(
                `Selection is out of date — refresh the review. (${e.message})`,
                { reportError: false },
              );
              return;
            case 'WORKFLOW_NOT_FOUND':
            case 'PROPOSAL_NOT_FOUND':
              toast.error(e.message, { reportError: false });
              onExit();
              return;
            default:
              // Fallback: 409 without a typed code still means stale.
              if (e.status === 409) {
                setStale(true);
                return;
              }
              toast.error(e.message ?? 'Unable to apply proposal.', {
                reportError: !e.userError,
              });
          }
        },
      },
    );
  }, [
    applyMutation,
    captureUndoSnapshot,
    decisions,
    diff,
    proposal,
    proposalId,
    toast,
    onExit,
  ]);

  const reject = useCallback(() => {
    rejectMutation.mutate(
      { proposalId },
      {
        onSuccess: () => {
          toast.success('Proposal rejected.');
          onExit();
        },
        onError: (err) => {
          const e = err as ApiError;
          toast.error(e.message ?? 'Unable to reject proposal.');
        },
      },
    );
  }, [rejectMutation, proposalId, toast, onExit]);

  // Stale proposals are dead on the server (status = 'stale'); there's no
  // revival path. Refresh drops the proposal from the local list and closes
  // review so the user lands back on the live designer with fresh data.
  const refresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['workflow-proposals'] });
    queryClient.invalidateQueries({ queryKey: ['workflow', workflowId] });
    toast.default(
      'The workflow changed since this proposal was created. A new proposal is needed to capture the latest edits.',
    );
    onExit();
  }, [queryClient, workflowId, toast, onExit]);

  const confirm = useCallback(() => {
    if (decisions.counts.accepted === 0) reject();
    else apply();
  }, [apply, reject, decisions.counts.accepted]);

  const confirmDisabled =
    applyMutation.isPending || rejectMutation.isPending || stale;
  const confirmPending = applyMutation.isPending || rejectMutation.isPending;

  return (
    <div className="flex h-full w-full flex-col">
      <ReviewHeader
        title={proposal?.title ?? 'Loading proposal…'}
        source={proposal?.source}
        stale={stale}
        onExit={onExit}
      />
      <div className="relative flex flex-1 flex-col overflow-hidden">
        {/* Expand/collapse-all rows — a floating canvas control pinned
            top-right so it stays put while the diff scrolls underneath. */}
        {proposal && rows.length > 0 && (
          <ReviewExpandCollapseControl
            hasExpandedRows={expanded.size > 0}
            onCollapseAll={collapseAll}
            onExpandAll={() => expandAll(rows.map((r) => r.key))}
          />
        )}
        <div className="flex flex-1 flex-col overflow-auto bg-muted/30 dark:bg-muted/10">
        {isLoading ? (
          <div className="flex flex-1 items-center justify-center">
            <Spinner />
          </div>
        ) : !proposal ? (
          <div className="flex flex-1 items-center justify-center">
            <Text className="text-muted-foreground">Proposal not found.</Text>
          </div>
        ) : (
          <>
            {proposal.reviewScorecard && (
              <div className="shrink-0 border-b border-border bg-card px-4 py-3">
                <ReviewScorecard
                  scorecard={proposal.reviewScorecard}
                  comments={proposal.comments ?? []}
                  collapsible
                />
              </div>
            )}
            {/* my-auto centers a short diff in the canvas; auto margins
                collapse to 0 once the content overflows, so a tall diff still
                scrolls from the top without clipping. */}
            <CompareGrid
              rows={rows}
              expanded={expanded}
              onToggle={toggleExpanded}
              decisions={decisions}
            />
          </>
        )}
        </div>
      </div>
      <ReviewActionBar
        stale={stale}
        decisions={decisions}
        onRefresh={refresh}
        onConfirm={confirm}
        confirmDisabled={confirmDisabled}
        confirmPending={confirmPending}
      />
    </div>
  );
}

function CompareGrid({
  rows,
  expanded,
  onToggle,
  decisions,
}: {
  rows: AlignedRow[];
  expanded: Set<string>;
  onToggle: (key: string) => void;
  decisions: ReviewDecisionsApi;
}) {
  const last = rows.length - 1;
  return (
    <div className="mx-auto my-auto w-full max-w-5xl p-8">
      <div
        className="grid gap-y-3"
        style={{ gridTemplateColumns: '1fr 7rem 1fr' }}
      >
        <ColumnHeader label="Current draft" tone="neutral" />
        <div />
        <ColumnHeader label="Proposed" tone="accent" />

        {rows.length === 0 ? (
          <div className="col-span-3 mt-3 rounded-lg border border-dashed border-border/60 bg-background/70 p-8 text-center">
            <Text className="text-muted-foreground">
              This proposal matches the current draft.
            </Text>
          </div>
        ) : (
          rows.map((row, idx) => (
            <CompareRow
              key={row.key}
              row={row}
              isFirst={idx === 0}
              isLast={idx === last}
              isExpanded={expanded.has(row.key)}
              onToggle={() => onToggle(row.key)}
              decisions={decisions}
            />
          ))
        )}
      </div>
    </div>
  );
}

function ColumnHeader({
  label,
  tone,
}: {
  label: string;
  tone: 'neutral' | 'accent';
}) {
  const classes =
    tone === 'accent'
      ? 'border-amber-300/70 bg-amber-50 text-amber-900 dark:border-amber-500/40 dark:bg-amber-950/30 dark:text-amber-200'
      : 'border-border bg-background text-foreground';
  return (
    <div
      className={`mb-1 rounded-md border px-3 py-1.5 text-center text-xs font-semibold uppercase tracking-wide ${classes}`}
    >
      {label}
    </div>
  );
}


/**
 * Node id that a change row's accept/reject decision is keyed on. For paired
 * (changed) rows both sides share the same id; for added / removed it's the
 * side that actually has a card.
 */
function decisionKeyForRow(row: AlignedRow): string | null {
  if (row.state === 'unchanged') return null;
  return row.current?.node.id ?? row.proposed?.node.id ?? null;
}

function CompareRow({
  row,
  isFirst,
  isLast,
  isExpanded,
  onToggle,
  decisions,
}: {
  row: AlignedRow;
  isFirst: boolean;
  isLast: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  decisions: ReviewDecisionsApi;
}) {
  const key = decisionKeyForRow(row);
  const decision: Decision | null =
    key && row.state !== 'unchanged' ? decisions.decisions[key] ?? 'accept' : null;
  const rejected = decision === 'reject';
  const accepted = decision === 'accept';

  // Mute whichever side is being replaced by the current decision:
  //  - accept a change/removal → current side goes away, mute it
  //  - reject a change/addition → proposed side doesn't apply, mute it
  // Unchanged rows have no decision and both sides stay at full intensity.
  const muteCurrent =
    accepted && (row.state === 'changed' || row.state === 'removed');
  const muteProposed =
    rejected && (row.state === 'changed' || row.state === 'added');

  const onSetDecision = (d: Decision) => {
    if (key) decisions.setDecision(key, d);
  };

  return (
    <>
      <SideCell
        slot={row.current}
        state={row.state}
        side="current"
        isFirst={isFirst}
        isLast={isLast}
        isExpanded={isExpanded}
        onToggle={onToggle}
        rowState={row.state}
        muted={muteCurrent}
        decision={decision}
        onSetDecision={onSetDecision}
        controlsOnThisSide={row.state === 'removed'}
      />
      <div className="relative flex items-center justify-center">
        {/* Faint rule threading the two sides together, with the change pill
            riding on top. Unchanged rows draw their own short tick instead. */}
        {row.state !== 'unchanged' && (
          <span
            aria-hidden
            className="absolute inset-x-1 top-1/2 h-px -translate-y-1/2 bg-border/60"
          />
        )}
        <span className="relative">
          <StateChip state={row.state} rejected={rejected} />
        </span>
      </div>
      <SideCell
        slot={row.proposed}
        state={row.state}
        side="proposed"
        isFirst={isFirst}
        isLast={isLast}
        isExpanded={isExpanded}
        onToggle={onToggle}
        rowState={row.state}
        muted={muteProposed}
        decision={decision}
        onSetDecision={onSetDecision}
        controlsOnThisSide={row.state === 'added' || row.state === 'changed'}
      />
      <AnimatePresence initial={false}>
        {isExpanded && (
          <>
            <ExpandedConfigCell
              key="c"
              slot={row.current}
              other={row.proposed}
              branchIndent={Boolean(row.current?.branch)}
              muted={muteCurrent}
            />
            <div key="m" aria-hidden />
            <ExpandedConfigCell
              key="p"
              slot={row.proposed}
              other={row.current}
              branchIndent={Boolean(row.proposed?.branch)}
              muted={muteProposed}
            />
          </>
        )}
      </AnimatePresence>
    </>
  );
}

function ExpandedConfigCell({
  slot,
  other,
  branchIndent,
  muted,
}: {
  slot: NodeWithBranch | null;
  other: NodeWithBranch | null;
  branchIndent: boolean;
  muted: boolean;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, height: 0, marginTop: 0 }}
      animate={{ opacity: 1, height: 'auto', marginTop: -4 }}
      exit={{ opacity: 0, height: 0, marginTop: 0 }}
      transition={{ duration: 0.22, ease: [0.22, 0.61, 0.36, 1] }}
      style={{ overflow: 'hidden' }}
      className={[
        'min-w-0',
        branchIndent ? 'pl-6' : '',
        muted ? 'opacity-60 saturate-50' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className="rounded-md border border-border/60 bg-background/70 p-3">
        {slot ? (
          <NodeConfigView
            config={slot.node.configJson}
            compareTo={other?.node.configJson}
            nodeType={slot.node.nodeType}
          />
        ) : (
          <Text size="sm" className="italic text-muted-foreground">
            Nothing on this side.
          </Text>
        )}
      </div>
    </motion.div>
  );
}


function SideCell({
  slot,
  state,
  side,
  isFirst,
  isLast,
  isExpanded,
  onToggle,
  rowState,
  muted,
  decision,
  onSetDecision,
  controlsOnThisSide,
}: {
  slot: NodeWithBranch | null;
  state: RowState;
  side: 'current' | 'proposed';
  isFirst: boolean;
  isLast: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  rowState: RowState;
  muted: boolean;
  decision: Decision | null;
  onSetDecision: (d: Decision) => void;
  controlsOnThisSide: boolean;
}) {
  const branch = slot?.branch ?? null;
  const content = (
    <SideSlot
      slot={slot}
      state={state}
      side={side}
      isExpanded={isExpanded}
      onToggle={onToggle}
      rowState={rowState}
      muted={muted}
      decision={controlsOnThisSide ? decision : null}
      onSetDecision={onSetDecision}
    />
  );
  if (!branch) return <div className="min-w-0">{content}</div>;
  return (
    <div className="relative min-w-0 pl-6">
      <span
        aria-hidden
        className={[
          'absolute left-3 w-0.5 rounded-full',
          branch === 'true'
            ? 'bg-emerald-300 dark:bg-emerald-500/60'
            : 'bg-rose-300 dark:bg-rose-500/60',
          isFirst ? 'top-0' : '-top-1.5',
          isLast ? 'bottom-0' : '-bottom-1.5',
        ].join(' ')}
      />
      {content}
    </div>
  );
}

function SideSlot({
  slot,
  state,
  side,
  isExpanded,
  onToggle,
  rowState,
  muted,
  decision,
  onSetDecision,
}: {
  slot: NodeWithBranch | null;
  state: RowState;
  side: 'current' | 'proposed';
  isExpanded: boolean;
  onToggle: () => void;
  rowState: RowState;
  muted: boolean;
  decision: Decision | null;
  onSetDecision: (d: Decision) => void;
}) {
  if (!slot) return <GapCard state={state} side={side} />;
  return (
    <div className="space-y-1.5">
      <NodeCard
        slot={slot}
        isExpanded={isExpanded}
        onToggle={onToggle}
        rowState={rowState}
        side={side}
        muted={muted}
      />
      {decision !== null && (
        <div className="flex justify-end">
          <DecisionBar decision={decision} onSet={onSetDecision} />
        </div>
      )}
    </div>
  );
}

function NodeCard({
  slot,
  isExpanded,
  onToggle,
  rowState,
  side,
  muted,
}: {
  slot: NodeWithBranch;
  isExpanded: boolean;
  onToggle: () => void;
  rowState: RowState;
  side: 'current' | 'proposed';
  muted: boolean;
}) {
  const { node, branch } = slot;
  const tone = nodeCardTone(rowState, side);
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={isExpanded}
      className={[
        'flex w-full min-w-0 items-center gap-2 rounded-lg border px-3 py-2.5 text-left shadow-sm transition-opacity',
        tone,
        isExpanded ? 'ring-1 ring-foreground/20' : '',
        muted ? 'opacity-60 saturate-50' : '',
      ].join(' ')}
    >
      <NodeIconPill nodeType={node.nodeType} size={14} />
      <div className="min-w-0 flex-1">
        <Flex align="center" gap="xs" className="min-w-0">
          <Text
            size="xs"
            className="uppercase tracking-wide text-muted-foreground"
          >
            {node.nodeType.replace(/_/g, ' ')}
          </Text>
          {branch && <BranchChip branch={branch} />}
        </Flex>
        <Text weight="medium" className="break-words text-sm">
          {getNodeDisplayTitle(node.nodeType, node.configJson)}
        </Text>
      </div>
      <span
        aria-hidden
        className="shrink-0 text-muted-foreground transition-transform duration-200"
        style={{ transform: `rotate(${isExpanded ? 180 : 0}deg)` }}
      >
        <CaretDown size={14} weight="bold" />
      </span>
    </button>
  );
}

const DECISION_OPTIONS: readonly PillToggleOption<Decision>[] = [
  {
    value: 'accept',
    label: 'Accept',
    icon: <Check size={11} weight="bold" />,
    color: 'blue',
  },
  {
    value: 'reject',
    label: 'Reject',
    icon: <X size={11} weight="bold" />,
    color: 'orange',
  },
];

function DecisionBar({
  decision,
  onSet,
}: {
  decision: Decision;
  onSet: (d: Decision) => void;
}) {
  return (
    <PillToggle<Decision>
      value={decision}
      onChange={onSet}
      options={DECISION_OPTIONS}
      aria-label="Accept or reject this change"
    />
  );
}

function nodeCardTone(state: RowState, side: 'current' | 'proposed'): string {
  if (state === 'added' && side === 'proposed') {
    return 'border-blue-300 bg-blue-50/50 dark:border-blue-500/40 dark:bg-blue-950/20';
  }
  if (state === 'removed' && side === 'current') {
    return 'border-orange-300 bg-orange-50/50 dark:border-orange-500/40 dark:bg-orange-950/20';
  }
  if (state === 'changed') {
    return 'border-amber-300 bg-amber-50/40 dark:border-amber-500/40 dark:bg-amber-950/15';
  }
  return 'border-border bg-card';
}

function BranchChip({ branch }: { branch: Exclude<BranchLabel, null> }) {
  const map = {
    true: {
      label: 'IF TRUE',
      cls: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300',
    },
    false: {
      label: 'IF FALSE',
      cls: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300',
    },
  } as const;
  const { label, cls } = map[branch];
  return (
    <span
      className={`inline-flex items-center rounded px-1.5 py-0 text-[9px] font-semibold uppercase tracking-wide ${cls}`}
    >
      {label}
    </span>
  );
}

function GapCard({ state, side }: { state: RowState; side: 'current' | 'proposed' }) {
  const label =
    state === 'added' && side === 'current'
      ? 'Not in current draft'
      : state === 'removed' && side === 'proposed'
        ? 'Removed in proposal'
        : '—';
  return (
    <div className="flex min-h-[3.5rem] items-center justify-center rounded-lg border border-dashed border-border/50 bg-background/30 px-3 py-2.5 text-xs italic text-muted-foreground/80">
      {label}
    </div>
  );
}

function StateChip({ state, rejected }: { state: RowState; rejected: boolean }) {
  if (state === 'unchanged') {
    return <span className="h-px w-6 bg-border" aria-hidden />;
  }
  const map: Record<
    Exclude<RowState, 'unchanged'>,
    { icon: React.ReactNode; label: string; cls: string }
  > = {
    added: {
      icon: <Plus size={10} weight="bold" />,
      label: 'Added',
      cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
    },
    removed: {
      icon: <Minus size={10} weight="bold" />,
      label: 'Removed',
      cls: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
    },
    changed: {
      icon: <PencilSimple size={10} weight="bold" />,
      label: 'Changed',
      cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
    },
  };
  const { icon, label, cls } = map[state];
  return (
    <span
      className={[
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium',
        cls,
        rejected ? 'opacity-60 line-through' : '',
      ].join(' ')}
    >
      {icon}
      {label}
    </span>
  );
}
